import { Link } from 'react-router-dom';
import { useQuery } from 'react-query';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Typography
} from '@mui/material';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import { contentAPI } from '../services/api';
import { PageHeader, EmptyState } from '../components/ui';
import './ModuleAdmin.css';

export default function ExpeditionList() {
  const { data: sectionRes, isLoading } = useQuery(
    ['expedition-section'],
    () => contentAPI.getExpeditionSection(),
    { staleTime: 60000 }
  );

  const { data: treksRes } = useQuery(
    ['treks-with-upcoming-batches'],
    () => contentAPI.getTreksWithUpcomingBatches()
  );

  const data = sectionRes?.data?.data;
  const mainTitle = data?.mainTitle ?? 'Expeditions';
  const sections = Array.isArray(data?.sections) ? data.sections : [];
  const treks = treksRes?.data?.data || [];
  const trekMap = new Map(treks.map((t) => [t.id, t]));

  return (
    <div className="module-page">
      <PageHeader
        title="Expedition"
        subtitle="View the expedition page structure: main title and sections with their assigned treks."
        actions={
          <Button component={Link} to="/expedition-page" variant="contained" startIcon={<SettingsRoundedIcon />}>
            Configure Expedition Page
          </Button>
        }
      />

      {isLoading ? (
        <Card>
          <CardContent>
            <div className="ops-loading-box">
              <CircularProgress size={28} sx={{ color: 'primary.main' }} />
              <Typography variant="body2" color="text.secondary">Loading expedition page…</Typography>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent>
            <Typography variant="overline" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
              Main title (page heading)
            </Typography>
            <Typography variant="h6" fontWeight={700} sx={{ mb: 2.5 }}>
              {mainTitle}
            </Typography>

            {sections.length === 0 ? (
              <EmptyState
                title="No sections configured yet"
                description='Use "Configure Expedition Page" to add sections and assign treks.'
              />
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {sections.map((sec, idx) => {
                  const trekIds = Array.isArray(sec.trekIds) ? sec.trekIds : [];
                  const fromApi = Array.isArray(sec.treks) ? sec.treks : [];
                  const resolvedTreks = fromApi.length
                    ? fromApi
                    : trekIds
                        .map((id) => trekMap.get(id))
                        .filter(Boolean);

                  return (
                    <Card key={idx} variant="outlined" sx={{ bgcolor: '#f4f6fb', boxShadow: 'none' }}>
                      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
                          {sec.title || `Section ${idx + 1}`}
                        </Typography>
                        {resolvedTreks.length > 0 ? (
                          <List dense disablePadding sx={{ pl: 1 }}>
                            {resolvedTreks.map((t) => (
                              <ListItem key={t.id} disableGutters sx={{ py: 0.25 }}>
                                <ListItemText
                                  primary={t.name}
                                  secondary={t.slug ? `/${t.slug}` : null}
                                  primaryTypographyProps={{ variant: 'body2', color: '#475569' }}
                                  secondaryTypographyProps={{ variant: 'caption', component: 'span', ml: 1 }}
                                />
                              </ListItem>
                            ))}
                          </List>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            No treks in this section.
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </Box>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
